import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Alert,
  Chip,
  IconButton,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import CreateEnvironmentDialog from '../../components/environment/dialogs/CreateEnvironmentDialog';
import EditEnvironmentDialog from '../../components/environment/dialogs/EditEnvironmentDialog';
import ArchiveConfirmDialog from '../../components/environment/dialogs/ArchiveConfirmDialog';
import DeleteConfirmDialog from '../../components/environment/dialogs/DeleteConfirmDialog';
import ConnectWabaDialog from '../../components/environment/dialogs/ConnectWabaDialog';
import RefreshWabaTokenDialog from '../../components/environment/dialogs/RefreshWabaTokenDialog';

// Helper function to calculate days remaining until token expiration
const getDaysRemaining = (tokenExpiresAt) => {
  if (!tokenExpiresAt) {
    return null;
  }
  const expiresAt = new Date(tokenExpiresAt);
  const now = new Date();
  const diffTime = expiresAt - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const EnvironmentsSettings = () => {
  const { selectedOrganization } = useOrganization();
  const { environments, selectedEnvironment, unarchiveEnvironment, getWabaConnection, loading, error } = useEnvironment();
  const { can } = usePermissions();
  const { plan, limits, showPlanLimit } = useEntitlements();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [connectWabaDialogOpen, setConnectWabaDialogOpen] = useState(false);
  const [refreshTokenDialogOpen, setRefreshTokenDialogOpen] = useState(false);
  const [selectedEnv, setSelectedEnv] = useState(null);
  const [replacingWaba, setReplacingWaba] = useState(false);

  const activeEnvironments = environments.filter(env => !env.isArchived);
  // Free is one environment; archived ones count, as they do on the server
  const environmentLocked = limits?.environments != null && environments.length >= limits.environments;
  const archivedEnvironments = environments.filter(env => env.isArchived);

  const handleEditClick = (env) => {
    setSelectedEnv(env);
    setEditDialogOpen(true);
  };

  const handleArchiveClick = (env) => {
    setSelectedEnv(env);
    setArchiveDialogOpen(true);
  };

  const handleUnarchiveClick = async (env) => {
    try {
      await unarchiveEnvironment(selectedOrganization.id, env.id);
    } catch (error) {
      console.error('Failed to unarchive environment:', error);
    }
  };

  const handleDeleteClick = (env) => {
    setSelectedEnv(env);
    setDeleteDialogOpen(true);
  };

  const handleConnectWabaClick = (env, replace = false) => {
    setSelectedEnv(env);
    setReplacingWaba(replace);
    setConnectWabaDialogOpen(true);
  };

  const handleRefreshTokenClick = (env) => {
    setSelectedEnv(env);
    setRefreshTokenDialogOpen(true);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Manage Environments
        </Typography>
        {can.createEnv && (
          <Button
            variant="contained"
            startIcon={environmentLocked ? <LockIcon /> : <AddIcon />}
            onClick={() => (environmentLocked
              ? showPlanLimit({ code: 'PLAN_LIMIT', limit: 'environments', plan })
              : setCreateDialogOpen(true))}
          >
            Create Environment
          </Button>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Active Environments Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
          Active Environments
        </Typography>

        {activeEnvironments.length === 0 ? (
          <Alert severity="info">
            No active environments. Click "Create Environment" to add one.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {activeEnvironments.map((env) => (
              <Grid item xs={12} sm={6} md={4} key={env.id}>
                <Card
                  sx={{
                    height: '100%',
                    border: selectedEnvironment?.id === env.id ? 2 : 0,
                    borderColor: 'primary.main'
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" component="div">
                        {env.name}
                      </Typography>
                      {selectedEnvironment?.id === env.id && (
                        <Chip label="Selected" color="primary" size="small" />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      Created: {new Date(env.createdAt).toLocaleDateString()}
                    </Typography>

                    {/* WABA Connection Status */}
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        WhatsApp Business Account
                      </Typography>
                      {(() => {
                        const wabaConnection = getWabaConnection(env.id);
                        if (wabaConnection) {
                          const daysRemaining = getDaysRemaining(wabaConnection.tokenExpiresAt);
                          const isExpiringSoon = daysRemaining !== null && daysRemaining <= 7;
                          const isExpired = daysRemaining !== null && daysRemaining <= 0;
                          return (
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                <CheckCircleIcon fontSize="small" color="success" />
                                <Typography variant="body2" color="success.main">
                                  {wabaConnection.wabaName || wabaConnection.wabaId}
                                </Typography>
                              </Box>
                              {/* Token expiration status */}
                              {daysRemaining !== null && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Chip
                                    size="small"
                                    icon={isExpiringSoon ? <WarningIcon /> : undefined}
                                    label={
                                      isExpired
                                        ? 'Token expired'
                                        : daysRemaining === 1
                                        ? '1 day remaining'
                                        : `${daysRemaining} days remaining`
                                    }
                                    color={
                                      isExpired
                                        ? 'error'
                                        : isExpiringSoon
                                        ? 'warning'
                                        : 'success'
                                    }
                                    variant="outlined"
                                  />
                                </Box>
                              )}
                              {/* Refresh token button for users with WABA modify permission */}
                              {can.modifyWaba && (
                                <Button
                                  size="small"
                                  startIcon={<RefreshIcon />}
                                  onClick={() => handleRefreshTokenClick(env)}
                                  color={isExpiringSoon ? 'warning' : 'primary'}
                                  sx={{ mt: 0.5 }}
                                >
                                  Refresh Token
                                </Button>
                              )}
                              {can.modifyWaba && can.disconnectWaba && (
                                <Button
                                  size="small"
                                  startIcon={<LinkIcon />}
                                  onClick={() => handleConnectWabaClick(env, true)}
                                  sx={{ mt: 0.5, ml: 1 }}
                                >
                                  Change number
                                </Button>
                              )}
                            </Box>
                          );
                        } else {
                          return (
                            <Box>
                              <Chip label="Not connected" size="small" color="warning" variant="outlined" />
                              {can.modifyWaba && (
                                <Button
                                  size="small"
                                  startIcon={<LinkIcon />}
                                  onClick={() => handleConnectWabaClick(env)}
                                  sx={{ mt: 1 }}
                                >
                                  Connect WABA
                                </Button>
                              )}
                            </Box>
                          );
                        }
                      })()}
                    </Box>
                  </CardContent>
                  {(can.modifyEnv || can.deleteEnv) && (
                    <CardActions>
                      {can.modifyEnv && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditClick(env)}
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {can.modifyEnv && (
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => handleArchiveClick(env)}
                          title="Archive"
                        >
                          <ArchiveIcon fontSize="small" />
                        </IconButton>
                      )}
                    </CardActions>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Archived Environments Section */}
      {archivedEnvironments.length > 0 && (
        <Box>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
            Archived Environments
          </Typography>

          <Grid container spacing={2}>
            {archivedEnvironments.map((env) => (
              <Grid item xs={12} sm={6} md={4} key={env.id}>
                <Card sx={{ height: '100%', opacity: 0.7 }}>
                  <CardContent>
                    <Typography variant="h6" component="div" sx={{ mb: 1 }}>
                      {env.name}
                    </Typography>
                    <Chip label="Archived" size="small" sx={{ mb: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block">
                      Archived: {new Date(env.archivedAt).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                  {(can.modifyEnv || can.deleteEnv) && (
                    <CardActions>
                      {can.modifyEnv && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleUnarchiveClick(env)}
                          title="Unarchive"
                        >
                          <UnarchiveIcon fontSize="small" />
                        </IconButton>
                      )}
                      {can.deleteEnv && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(env)}
                          title="Delete Permanently"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </CardActions>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Dialogs */}
      <CreateEnvironmentDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />

      {selectedEnv && (
        <>
          <EditEnvironmentDialog
            open={editDialogOpen}
            onClose={() => {
              setEditDialogOpen(false);
              setSelectedEnv(null);
            }}
            environment={selectedEnv}
          />

          <ArchiveConfirmDialog
            open={archiveDialogOpen}
            onClose={() => {
              setArchiveDialogOpen(false);
              setSelectedEnv(null);
            }}
            environment={selectedEnv}
          />

          <DeleteConfirmDialog
            open={deleteDialogOpen}
            onClose={() => {
              setDeleteDialogOpen(false);
              setSelectedEnv(null);
            }}
            environment={selectedEnv}
          />

          <ConnectWabaDialog
            open={connectWabaDialogOpen}
            onClose={() => {
              setConnectWabaDialogOpen(false);
              setSelectedEnv(null);
            }}
            environment={selectedEnv}
            replace={replacingWaba}
          />

          <RefreshWabaTokenDialog
            open={refreshTokenDialogOpen}
            onClose={() => {
              setRefreshTokenDialogOpen(false);
              setSelectedEnv(null);
            }}
            environment={selectedEnv}
            wabaConnection={selectedEnv ? getWabaConnection(selectedEnv.id) : null}
          />
        </>
      )}
    </Box>
  );
};

export default EnvironmentsSettings;
